import React, { useState, useEffect, useRef } from 'react'
import { IAccountDeletedFilters, IFilterConfig } from '../types'
import { EFilterType, EFilterOperator, EFilterSpecOperator, EFilterFetchConvertType } from '../types/enum'
import { constructNewFilterFromConfig } from '../components/filters'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'
import { ROLE_EXTRA_IS_BUYER_OPTIONS, ACCOUNT_IS_ACTIVE_OPTIONS, ACCOUNT_IS_FIRED_OPTIONS } from '../constants'

type IUseFiltersAccountDeletedListModal = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	accountDeletedFiltersConfig: IFilterConfig<keyof IAccountDeletedFilters>[];
	defaultFiltersValue: IAccountDeletedFilters;
	initialFiltersValue: IAccountDeletedFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IAccountDeletedFilters | null)) => (IAccountDeletedFilters | null)) | IAccountDeletedFilters | null)) => void;
}

const ACCOUNT_DELETED_FILTERS_DEFAULT: IAccountDeletedFilters = {
	email: '',
	'auth.login': '',
	'role.name': '',
	roleId: null,
	isActive: null,
	isFired: null,
	registeredAt: '',
	'extra.keitaroLogin': '',
	'role.extra.inGroup': '',
	'role.extra.isBuyer': null
}
export const useFiltersAccountDeletedListModal = (
	setPage: React.Dispatch<React.SetStateAction<number>>
): IUseFiltersAccountDeletedListModal => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)

	const [filter, setFilter] = useState<[string, any][]>([]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IAccountDeletedFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const ROLE_EXTRA_IS_BUYER_OPTIONS_TRANS = ROLE_EXTRA_IS_BUYER_OPTIONS(t)
	const ACCOUNT_IS_ACTIVE_OPTIONS_TRANS = ACCOUNT_IS_ACTIVE_OPTIONS(t)
	const ACCOUNT_IS_FIRED_OPTIONS_TRANS = ACCOUNT_IS_FIRED_OPTIONS(t)

	const accountDeletedFiltersConfig: IFilterConfig<keyof IAccountDeletedFilters>[] = [
		// {
		// 	name: 'code',
		// 	label: 'Code',
		// 	type: EFilterType.input,
		// 	operator: EFilterOperator.isLike,
		// 	specOperator: EFilterSpecOperator.everywhere,
		// 	styles: { width: '200px' }
		// },
		{
			name: 'email',
			label: t(ALL_LANG_KEY.account_list_table.column_email),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'auth.login',
			label: t(ALL_LANG_KEY.account_list_table.column_login),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'extra.keitaroLogin',
			label: t(ALL_LANG_KEY.account_list_table.column_kt_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.name',
			label: t(ALL_LANG_KEY.account_list_table.column_role),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.extra.isBuyer',
			label: t(ALL_LANG_KEY.account_list_table.column_is_buyer),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ROLE_EXTRA_IS_BUYER_OPTIONS_TRANS,
			styles: { minWidth: '200px' }
		},
		{
			name: 'role.extra.inGroup',
			label: t(ALL_LANG_KEY.account_list_table.column_in_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'isActive',
			label: t(ALL_LANG_KEY.account_list_table.column_is_active),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ACCOUNT_IS_ACTIVE_OPTIONS_TRANS,
			styles: { minWidth: '160px' }
		},
		{
			name: 'isFired',
			label: t(ALL_LANG_KEY.account_list_table.column_is_fired),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ACCOUNT_IS_FIRED_OPTIONS_TRANS,
			styles: { minWidth: '160px' }
		}
		// {
		// 	name: 'registeredAt',
		// 	label: 'Registered At',
		// 	type: EFilterType.input,
		// 	operator: EFilterOperator.isLike,
		// 	specOperator: EFilterSpecOperator.everywhere,
		// 	styles: { width: '200px' }
		// },
	]

	useEffect(() => {
		//console.log('hook.useFiltersAccountDeletedListModal useEffect::START:: ', { userFilters, filter })
		if (!!userFilters) {
			setSend(true)

			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				userFilters,
				accountDeletedFiltersConfig
			)

			// while first render page resetting is not needed
			if (!isFirstRender.current) {
				setPage(0)
			} else {
				isFirstRender.current = false
			}

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)
			//console.log('hook.useFiltersAccountDeletedListModal:: useEffect::END:: ', { userFilters, filter, newFilter })
		}
	}, [userFilters])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		accountDeletedFiltersConfig,
		defaultFiltersValue: ACCOUNT_DELETED_FILTERS_DEFAULT,
		initialFiltersValue: ACCOUNT_DELETED_FILTERS_DEFAULT,
		clearChildFilters,
		setUserFilters
	}
}